import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, CheckCircle2, ShieldCheck, ArrowUpRight, Receipt, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_STYLES = {
  ok: {
    bar: 'bg-emerald-500',
    track: 'bg-emerald-100',
    text: 'text-emerald-700',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle2,
    label: 'Limita robežās',
  },
  approaching_limit: {
    bar: 'bg-amber-500',
    track: 'bg-amber-100',
    text: 'text-amber-700',
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: AlertTriangle,
    label: 'Tuvojies limitam',
  },
  at_limit: {
    bar: 'bg-red-500',
    track: 'bg-red-100',
    text: 'text-red-700',
    chip: 'bg-red-50 text-red-700 border-red-200',
    icon: AlertTriangle,
    label: 'Sasniegts limits',
  },
};

const TYPE_LABELS = {
  private: 'Privātpersona',
  self_employed: 'Pašnodarbinātais',
  business: 'Uzņēmums (SIA/IK)',
};

function ProgressRow({ label, current, limit, status, suffix = '' }) {
  const pct = limit ? Math.min(100, (current / limit) * 100) : 0;
  const style = STATUS_STYLES[status] || STATUS_STYLES.ok;
  return (
    <div data-testid={`compliance-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-medium text-[#121212]">{label}</span>
        <span className={`text-sm font-semibold ${style.text}`}>
          {current.toLocaleString('lv-LV', { maximumFractionDigits: 2 })}{suffix} / {limit.toLocaleString('lv-LV')}{suffix}
        </span>
      </div>
      <div className={`relative h-2 rounded-full overflow-hidden ${style.track}`}>
        <div
          className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${style.bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function BusinessThresholdCard({ data, onReload }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(data.external_turnover_eur || 0));
  const [saving, setSaving] = useState(false);

  const vatRegistered = data.vat_registered;
  const status = vatRegistered ? 'ok' : (data.usage_pct >= 100 ? 'at_limit' : data.usage_pct >= 80 ? 'approaching_limit' : 'ok');
  const style = STATUS_STYLES[status] || STATUS_STYLES.ok;
  const Icon = vatRegistered ? ShieldCheck : style.icon;
  const remaining = Math.max(0, (data.vat_threshold_eur || 0) - (data.total_turnover_eur || 0));

  const save = async () => {
    setSaving(true);
    try {
      const amount = parseFloat(String(val).replace(',', '.')) || 0;
      await axios.post(`${API}/seller/me/external-turnover`, { amount, year: data.year });
      const { data: fresh } = await axios.get(`${API}/seller/me/compliance`);
      onReload(fresh);
      setEditing(false);
    } catch (e) { alert('Neizdevās saglabāt'); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 sm:p-6" data-testid="business-threshold-widget">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${vatRegistered ? 'bg-emerald-50' : style.track}`}>
            <Icon className={`w-5 h-5 ${vatRegistered ? 'text-emerald-600' : style.text}`} />
          </div>
          <div>
            <h3 className="font-outfit text-lg font-semibold text-[#121212]">PVN reģistrācijas slieksnis</h3>
            <p className="text-xs text-[#5C5C5C]">{TYPE_LABELS[data.seller_type] || data.seller_type} · {data.year}. gada apgrozījums</p>
          </div>
        </div>
        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${vatRegistered ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : style.chip}`} data-testid="business-status-chip">
          {vatRegistered ? 'PVN maksātājs' : status === 'at_limit' ? 'Sasniegts slieksnis' : status === 'approaching_limit' ? 'Tuvojies slieksnim' : 'Sliekšņa robežās'}
        </span>
      </div>

      <ProgressRow
        label="Kopējais apgrozījums"
        current={data.total_turnover_eur || 0}
        limit={data.vat_threshold_eur || 50000}
        status={status}
        suffix=" €"
      />

      <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
        <div className="p-3 rounded-xl bg-[#FAFAFA] border border-[#F3F2EE]" data-testid="kolv-turnover">
          <p className="text-xs text-[#9CA3AF]">KoLV apgrozījums</p>
          <p className="font-semibold text-[#121212]">€{(data.kolv_turnover_eur || 0).toLocaleString('lv-LV', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="p-3 rounded-xl bg-[#FAFAFA] border border-[#F3F2EE]" data-testid="external-turnover">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#9CA3AF]">Ārpus KoLV (deklarēts)</p>
            {!editing && <button onClick={() => setEditing(true)} className="text-[#4A6B53]" data-testid="edit-external-turnover"><Pencil className="w-3 h-3" /></button>}
          </div>
          {editing ? (
            <div className="flex items-center gap-1 mt-1">
              <input type="number" min="0" step="0.01" value={val} onChange={(e) => setVal(e.target.value)}
                className="w-full text-sm border border-[#E5E4E0] rounded-md px-1.5 py-0.5" data-testid="external-turnover-input" autoFocus />
              <button onClick={save} disabled={saving} className="text-xs px-2 py-1 bg-[#4A6B53] text-white rounded-md" data-testid="save-external-turnover">OK</button>
            </div>
          ) : (
            <p className="font-semibold text-[#121212]">€{(data.external_turnover_eur || 0).toLocaleString('lv-LV', { minimumFractionDigits: 2 })}</p>
          )}
        </div>
      </div>

      {!vatRegistered && (
        <div className={`mt-4 p-3 rounded-xl border text-sm ${data.usage_pct >= 80 ? style.chip : 'bg-[#FAFAFA] text-[#5C5C5C] border-[#F3F2EE]'}`} data-testid="business-threshold-note">
          {data.usage_pct >= 100 ? (
            <><strong>Sasniegts €{(data.vat_threshold_eur || 0).toLocaleString('lv-LV')} slieksnis.</strong> Tev ir pienākums reģistrēties kā PVN maksātājam VID. Tas ir tikai apgrozījums (ne peļņa).</>
          ) : data.usage_pct >= 80 ? (
            <><strong>Tuvojies PVN slieksnim.</strong> Atlikuši €{remaining.toLocaleString('lv-LV', { minimumFractionDigits: 2 })} līdz obligātai PVN reģistrācijai. Plāno savlaicīgi.</>
          ) : (
            <>Līdz PVN reģistrācijas pienākumam atlikuši <strong>€{remaining.toLocaleString('lv-LV', { minimumFractionDigits: 2 })}</strong> apgrozījuma. Ievadi ārpus KoLV apgrozījumu precīzai uzskaitei.</>
          )}
        </div>
      )}
      {vatRegistered && (
        <div className="mt-4 p-3 rounded-xl border bg-emerald-50 text-emerald-700 border-emerald-200 text-sm flex items-center gap-2" data-testid="business-vat-ok">
          <Receipt className="w-4 h-4" /> Tu esi PVN maksātājs — slieksnis nav piemērojams. Apgrozījums redzams uzskaitei.
        </div>
      )}
    </div>
  );
}


export default function ComplianceWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/seller/me/compliance`)
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => { /* not a seller / not logged in — hide silently */ })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading || !data) return null;

  // Business / self-employed: VAT-registration threshold tracker with an
  // input for off-platform turnover (helps bookkeeping).
  if (data.type === 'business_vat') {
    return <BusinessThresholdCard data={data} onReload={(d) => setData(d)} />;
  }

  // Non-private sellers without a threshold report — friendly confirmation.
  if (data.applicable === false) {
    return (
      <div
        className="bg-white border border-[#E5E4E0] rounded-2xl p-5 sm:p-6"
        data-testid="compliance-widget-business"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-outfit text-lg font-semibold text-[#121212]">
              Saimnieciskās darbības statuss
            </h3>
            <p className="text-sm text-[#5C5C5C]">
              {TYPE_LABELS[data.seller_type] || data.seller_type}: bez papildu KoLV limitiem.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const style = STATUS_STYLES[data.status] || STATUS_STYLES.ok;
  const Icon = style.icon;

  return (
    <div
      className="bg-white border border-[#E5E4E0] rounded-2xl p-5 sm:p-6"
      data-testid="compliance-widget"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${style.track}`}>
            <Icon className={`w-5 h-5 ${style.text}`} />
          </div>
          <div>
            <h3 className="font-outfit text-lg font-semibold text-[#121212]">
              Privātpersonas limiti
            </h3>
            <p className="text-xs text-[#5C5C5C]">
              DAC7 + AML noteikumi · {data.year}. gads
            </p>
          </div>
        </div>
        <span
          className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${style.chip}`}
          data-testid="compliance-status-chip"
        >
          {style.label}
        </span>
      </div>

      <div className="space-y-4">
        <ProgressRow
          label="Apgrozījums šogad"
          current={data.ytd_revenue || 0}
          limit={data.limits.yearly_revenue_eur}
          status={data.revenue_pct >= 100 ? 'at_limit' : data.revenue_pct >= 80 ? 'approaching_limit' : 'ok'}
          suffix=" €"
        />
        <ProgressRow
          label="Darījumi šogad"
          current={data.ytd_transactions || 0}
          limit={data.limits.yearly_transactions}
          status={data.transactions_pct >= 100 ? 'at_limit' : data.transactions_pct >= 80 ? 'approaching_limit' : 'ok'}
        />
        <ProgressRow
          label="Izmaksas šomēnes"
          current={data.monthly_payouts_eur || 0}
          limit={data.limits.monthly_payout_eur}
          status={
            (data.monthly_payouts_eur || 0) >= data.limits.monthly_payout_eur
              ? 'at_limit'
              : (data.monthly_payouts_eur || 0) >= data.limits.monthly_payout_eur * 0.8
                ? 'approaching_limit'
                : 'ok'
          }
          suffix=" €"
        />
      </div>

      {data.status !== 'ok' && (
        <div className={`mt-4 p-3 rounded-xl border text-sm ${style.chip}`} data-testid="compliance-warning">
          {data.status === 'at_limit' ? (
            <>
              <strong>Sasniegts privātpersonas limits.</strong> Tavi sludinājumi tiek paslēpti, līdz pārreģistrējies kā pašnodarbinātais vai SIA.
            </>
          ) : (
            <>
              <strong>Tuvojies limitam.</strong> Iesakām reģistrēt saimnieciskās darbības statusu, lai nepārtrauktu pārdošanu.
            </>
          )}
        </div>
      )}

      <Link
        to="/seller/upgrade-status"
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-[#D97736] hover:bg-[#C06528] text-white text-sm font-medium rounded-xl transition-colors"
        data-testid="compliance-upgrade-cta"
      >
        Atjaunot pārdevēja statusu <ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
